import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Popover } from "@mui/material";
import {
  TbAdjustmentsHorizontal,
  TbCalendar,
  TbChevronDown,
  TbCoin,
  TbCreditCard,
  TbCurrencyReal,
  TbDownload,
  TbHash,
  TbId,
  TbSearch,
  TbSwitchHorizontal,
  TbTrash,
  TbX,
} from "react-icons/tb";
import { FilterDialog } from "./FilterDialog";
import { useToast } from "../../../Hooks/toast";
import { PanelActions } from "./ModernFilterPanels";

export const FILTER_CHIP_ICONS = {
  date: TbCalendar,
  amount: TbCurrencyReal,
  type: TbSwitchHorizontal,
  subAccount: TbAdjustmentsHorizontal,
  search: TbSearch,
  currency: TbCoin,
  status: TbAdjustmentsHorizontal,
  payment: TbCreditCard,
  trace: TbHash,
  external: TbId,
};

const DropdownButton = ({ icon: Icon, label, active, onClick }) => (
  <button
    type="button"
    className={`extract-filter-btn ${active ? "extract-filter-btn--active" : ""}`}
    onClick={onClick}
    aria-expanded={active}
  >
    <Icon className="extract-filter-btn__icon" aria-hidden />
    <span className="extract-filter-btn__label">{label}</span>
    <TbChevronDown className="extract-filter-btn__chevron" aria-hidden />
  </button>
);

export const ModernFilterBar = ({
  filters,
  onFiltersChange,
  emptyFilters,
  searchPlaceholder = intl.get("extract.searchTransaction"),
  searchQueryKey = "searchQuery",
  showSearch = true,
  filterButtons = [],
  moreFiltersActive = false,
  buildFilterTags,
  hasActiveFilters,
  removeFilterTag,
  onExport,
  onExportClick,
  renderPanelContent,
  renderMoreFilters,
  panelTitles = {},
  showFilters = true,
  leadingActions = null,
  filteredItems = [],
  moreFiltersClassName = "",
  exportEmptyMessageKey = "common.noRecordsFiltered",
}) => {
  const toast = useToast();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const [draft, setDraft] = useState(filters);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(filters?.[searchQueryKey] ?? "");

  useEffect(() => {
    setSearchValue(filters?.[searchQueryKey] ?? "");
  }, [filters, searchQueryKey]);

  const appliedTags = useMemo(
    () => (buildFilterTags ? buildFilterTags(filters) : []),
    [buildFilterTags, filters],
  );
  const filtersActive = hasActiveFilters ? hasActiveFilters(filters) : false;

  const closePanel = () => {
    setAnchorEl(null);
    setActivePanel(null);
  };

  const openMoreFilters = () => {
    if (!renderMoreFilters) return;
    closePanel();
    setDraft({ ...filters });
    setMoreFiltersOpen(true);
  };

  const openPanel = (event, panelId) => {
    if (panelId === "more") {
      openMoreFilters();
      return;
    }
    setDraft({ ...filters });
    setActivePanel(panelId);
    setAnchorEl(event.currentTarget);
  };

  const applyDraft = () => {
    onFiltersChange(draft);
    closePanel();
    setMoreFiltersOpen(false);
  };

  const handleClearAll = () => {
    onFiltersChange(emptyFilters);
    setDraft(emptyFilters);
    setSearchValue("");
    closePanel();
    setMoreFiltersOpen(false);
  };

  const handleRemoveTag = (tag) => {
    if (!removeFilterTag) return;
    onFiltersChange(removeFilterTag(filters, tag.key, tag));
  };

  const handleChipClick = (event) => {
    event.stopPropagation();
    openMoreFilters();
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    onFiltersChange({ ...filters, [searchQueryKey]: value });
  };

  const handleExport = () => {
    if (onExportClick) {
      onExportClick();
      return;
    }

    if (onExport) {
      if (filteredItems.length === 0) {
        toast.addToast({
          type: "info",
          title: intl.get("common.nothingToExport"),
          description: intl.get(exportEmptyMessageKey),
        });
        return;
      }
      onExport(filteredItems);
      toast.addToast({
        type: "success",
        title: intl.get("common.exportComplete"),
        description: intl.get("common.exportCompleteCsv", { count: filteredItems.length }),
      });
    }
  };

  const isDatePanel = activePanel === "date";

  return (
    <div className="extract-modern-filter">
      <div className="extract-modern-filter__toolbar">
        {showFilters ? (
          <div className="extract-modern-filter__row">
            {showSearch ? (
              <div className="extract-modern-filter__search">
                <TbSearch className="extract-modern-filter__search-icon" aria-hidden />
                <input
                  type="search"
                  className="extract-modern-filter__search-input"
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={handleSearchChange}
                  aria-label={searchPlaceholder}
                />
              </div>
            ) : null}

            <div className="extract-modern-filter__buttons">
              {filterButtons.map((button) => (
                <DropdownButton
                  key={button.id}
                  icon={button.icon}
                  label={button.label}
                  active={button.active}
                  onClick={(event) => openPanel(event, button.id)}
                />
              ))}
              {renderMoreFilters ? (
                <DropdownButton
                  icon={TbAdjustmentsHorizontal}
                  label={intl.get("common.moreFilters")}
                  active={moreFiltersActive}
                  onClick={(event) => openPanel(event, "more")}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="extract-modern-filter__actions">
          {leadingActions}
          {(onExport || onExportClick) ? (
            <button type="button" className="grid-history-action-btn" onClick={handleExport}>
              <TbDownload className="h-4 w-4" aria-hidden />
              {intl.get("common.export")}
            </button>
          ) : null}
        </div>
      </div>

      {showFilters && (filtersActive || appliedTags.length > 0) && (
        <div className="extract-modern-filter__active-row">
          <span className="extract-modern-filter__active-label">
            {intl.get("extract.activeFilters")}
          </span>
          <div className="extract-modern-filter__active-chips">
            {appliedTags.map((tag) => {
              const TagIcon = FILTER_CHIP_ICONS[tag.iconType] || TbAdjustmentsHorizontal;
              return (
                <button
                  key={tag.key}
                  type="button"
                  className={`extract-active-chip extract-active-chip--${tag.iconType}`}
                  onClick={handleChipClick}
                >
                  <span className="extract-active-chip__icon">
                    <TagIcon aria-hidden />
                  </span>
                  <span className="extract-active-chip__value">{tag.value}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="extract-active-chip__remove"
                    aria-label={intl.get("words.clear")}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRemoveTag(tag);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        handleRemoveTag(tag);
                      }
                    }}
                  >
                    <TbX aria-hidden />
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="extract-modern-filter__clear-all"
            onClick={handleClearAll}
          >
            <TbTrash aria-hidden />
            {intl.get("extract.clearAll")}
          </button>
        </div>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePanel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          className: `extract-filter-panel${isDatePanel ? " extract-filter-panel--date" : ""}`.trim(),
          sx: {
            mt: 1,
            width: {
              xs: isDatePanel ? "min(300px, calc(100vw - 2rem))" : "calc(100vw - 2rem)",
              sm: isDatePanel ? "auto" : 320,
            },
            maxWidth: isDatePanel
              ? { xs: "calc(100vw - 2rem)", sm: "min(560px, calc(100vw - 2rem))" }
              : "calc(100vw - 2rem)",
          },
        }}
      >
        {activePanel && renderPanelContent ? (
          <>
            <p className="extract-filter-panel__title">{panelTitles[activePanel]}</p>
            {renderPanelContent(activePanel, draft, setDraft)}
            <PanelActions onCancel={closePanel} onApply={applyDraft} />
          </>
        ) : null}
      </Popover>

      {renderMoreFilters ? (
        <FilterDialog
          className={`filter-dialog--extract-more ${moreFiltersClassName}`.trim()}
          open={moreFiltersOpen}
          onClose={() => setMoreFiltersOpen(false)}
          footer={(
            <>
              <button type="button" className="grid-history-action-btn" onClick={handleClearAll}>
                {intl.get("words.clear")}
              </button>
              <button
                type="button"
                className="grid-history-action-btn grid-history-action-btn--primary"
                onClick={applyDraft}
              >
                {intl.get("common.filter")}
              </button>
            </>
          )}
        >
          {renderMoreFilters(draft, setDraft)}
        </FilterDialog>
      ) : null}
    </div>
  );
};
